import React from "react";
import { Helmet } from "react-helmet-async";
import { useI18n } from "../lib/i18n";

const ORIGIN = "https://aldakheellg.com";

/**
 * SEO — drops <title>, description, canonical, OG, Twitter, and any
 * optional JSON-LD into <head> for the active page.
 *
 * Usage:
 *   <SEO titleAr="..." titleEn="..." descriptionAr="..." path="/about" />
 *   <SEO ... jsonLd={[{ "@context": ..., "@type": "FAQPage", ... }]} />
 */
export default function SEO({
  titleAr,
  titleEn,
  descriptionAr,
  descriptionEn,
  path = "",
  image = "/branding/icon-512.png",
  jsonLd,
  noindex = false,
}) {
  const { lang } = useI18n();
  const title =
    lang === "ar"
      ? titleAr || titleEn || "مجموعة الدخيل القانونية"
      : titleEn || titleAr || "Aldakheel Legal Group";
  const description =
    lang === "ar"
      ? descriptionAr || descriptionEn || ""
      : descriptionEn || descriptionAr || "";
  const canonical = `${ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
  const imageUrl = image.startsWith("http") ? image : `${ORIGIN}${image}`;

  return (
    <Helmet>
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={lang === "ar" ? "ar_KW" : "en_US"} />
      <meta property="og:site_name" content="Aldakheel Legal Group" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Hreflang alternates */}
      <link rel="alternate" hrefLang="ar" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((blob, i) => (
        <script
          key={`jsonld-${i}`}
          type="application/ld+json"
          // JSON-LD payloads come from in-repo builders only (orgJsonLd,
          // faqJsonLd, breadcrumbJsonLd, articleJsonLd) — never user input.
          // We still escape `</` → `<\/` (RFC 7159 §7 / OWASP advice) so a
          // future change that introduces dynamic data can't break out of
          // the script tag.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(blob).replace(/<\/(script)/gi, "<\\/$1"),
          }}
        />
      ))}
    </Helmet>
  );
}

// Common JSON-LD builders.
export const orgJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: "Aldakheel Legal Group",
  alternateName: "مجموعة الدخيل القانونية",
  url: ORIGIN,
  logo: `${ORIGIN}/branding/icon-512.png`,
  image: `${ORIGIN}/branding/icon-512.png`,
  description:
    "Kuwait-licensed law firm offering consultation, AI legal assistant, contract drafting, and litigation.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Kuwait City",
    addressCountry: "KW",
  },
  telephone: "+965-6965-6963",
  email: "contact@aldakheel.legal",
  areaServed: { "@type": "Country", "name": "Kuwait" },
  priceRange: "KWD",
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${ORIGIN}${item.path}`,
  })),
});

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", "text": f.a },
  })),
});

export const articleJsonLd = ({ title, description, datePublished, path }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  datePublished,
  author: { "@type": "Organization", name: "Aldakheel Legal Group" },
  publisher: {
    "@type": "Organization",
    name: "Aldakheel Legal Group",
    logo: { "@type": "ImageObject", url: `${ORIGIN}/branding/icon-512.png` },
  },
  mainEntityOfPage: `${ORIGIN}${path}`,
});
